import type { UserColor } from '@ui/components/features/calendar/types';

export type { UserColor };

/**
 * Calendar event palette — "dusty rainbow" variant, app-tone-first.
 *
 * Design goals:
 * - Colors span the full hue wheel (~45° apart) so any two activities
 *   are visibly distinct, even at small sizes.
 * - Stay pastel / muted (S ~40–55%, bg L ~85–88%) so cards still feel
 *   soft and on-brand, not "primary color blocks".
 * - The FIRST entries echo the app's brand palette, so a professional
 *   with only 2–3 activities sees colors that feel consistent with the
 *   rest of the product:
 *     1. lilac     — brand purple `#7a5af8`
 *     2. coral     — brand primary `#fb5014` (desaturated to avoid clash)
 *     3. sky       — brand info    `#3499fd`
 *     4. mint      — brand success `#41a641`
 *     5. amber     — brand warning `#ecae06`
 *   Entries 6–8 (pink / periwinkle / teal) are variety colors that fill
 *   the remaining gaps on the hue wheel. Only one "true green" (mint)
 *   is kept; teal sits on the cool blue-green side so the palette
 *   doesn't end up with two similar green cards.
 * - Stay clear of the brand primary `#fb5014` as a solid fill (very
 *   saturated orange). Coral is intentionally desaturated and pushed
 *   a few degrees toward red so it reads as a sibling, not a clone.
 * - No gray. The calendar already uses grayscale for past / disabled
 *   states, so a gray activity color reads as "unavailable".
 *
 * Color construction rhythm:
 *   background — light dusty tint (~85–88% lightness)
 *   border     — mid-tone hue (~45–55% lightness), used for the left bar
 *   text       — deep hue (~20% lightness), AA contrast on the background
 *   subText    — one step lighter than text
 */
const COLOR_PALETTE: Record<string, UserColor> = {
  lilac: {
    background: '#DCD6F0',
    border: '#6D5AC0',
    text: '#231C54',
    subText: '#3B3182'
  },
  coral: {
    background: '#F5D4D0',
    border: '#C75247',
    text: '#541E1A',
    subText: '#823931'
  },
  sky: {
    background: '#C5DFEA',
    border: '#3987B4',
    text: '#0F3A50',
    subText: '#1F5778'
  },
  mint: {
    background: '#CFE5CB',
    border: '#4E9A48',
    text: '#19401A',
    subText: '#2E6B2E'
  },
  amber: {
    background: '#F2E2C0',
    border: '#BE822F',
    text: '#523315',
    subText: '#7B5420'
  },
  pink: {
    background: '#F0D0DF',
    border: '#C44E85',
    text: '#541E3A',
    subText: '#82345D'
  },
  periwinkle: {
    background: '#D1DCEB',
    border: '#5B7BAF',
    text: '#1A2E4C',
    subText: '#334C7C'
  },
  teal: {
    background: '#CAE5E7',
    border: '#3B939B',
    text: '#123B3F',
    subText: '#25646A'
  }
};

const COLOR_KEYS = Object.keys(COLOR_PALETTE) as Array<keyof typeof COLOR_PALETTE>;

function generateActivityHash(activityId: string): number {
  let hash = 0;
  const normalizedId = activityId.toLowerCase().trim();

  for (let i = 0; i < normalizedId.length; i++) {
    const char = normalizedId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

/** The palette colour at a position in a list of identifiers; past the palette (or absent), one picked from the id. */
export function colorAtPosition(id: string, position: number): UserColor {
  if (position !== -1 && position < COLOR_KEYS.length) return COLOR_PALETTE[COLOR_KEYS[position]];
  return COLOR_PALETTE[COLOR_KEYS[generateActivityHash(id) % COLOR_KEYS.length]];
}

export function getActivityColorWithContext(activityId: string, allActivityIdentifiers: string[] = []): UserColor {
  return colorAtPosition(activityId, [...allActivityIdentifiers].sort().indexOf(activityId));
}

export function extractUniqueActivityIdentifiers(
  bookings: Array<{
    activityId: string;
  }>
): string[] {
  const identifiers = new Set<string>();

  bookings.forEach((booking) => {
    if (booking.activityId) {
      identifiers.add(booking.activityId);
    }
  });

  return Array.from(identifiers).sort();
}
